import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import moment from 'moment'

interface AdminTeacher {
  fullName: string | null
  firstName: string | null
  lastName: string | null
  emailAddress: string | null
  id: number | null
  schoolName: string | null
  validated: boolean | null
  validationToken: string | null
  invitedDate: string | null
  schoolState: string | null
  schoolId: number | null
  mostRecentCourseStartDate: string | null
  isQASchool: boolean | null
  teacherVerificationStatus: string | null
  jobTitle: string | null
  educatorType: string | null
  signUpDistrict: string | null
  signUpCounty: string | null
  signUpState: string | null
  signUpCountry: string | null
  signUpSchoolName: string | null
}

export const selectTeachers = (state: any) => state.teachers
export const selectTeacher = (id: number) => (state: any) => state.teachers.find((teacher: AdminTeacher) => teacher.id === id)
export const selectPendingTeachers = (state: any) => state.teachers.filter((teacher: AdminTeacher) => (teacher.teacherVerificationStatus === TEACHER_VERIFICATION_STATUS.PENDING && teacher.validated))
export const selectRestrictedTeachers = (state: any) => state.teachers.filter((teacher: AdminTeacher) => (teacher.teacherVerificationStatus === TEACHER_VERIFICATION_STATUS.KEEP_RESTRICTED || teacher.teacherVerificationStatus === TEACHER_VERIFICATION_STATUS.K8_RESTRICTED)).sort((a: AdminTeacher, b: AdminTeacher) => (a.id ?? 0) > (b.id ?? 0) ? -1 : 1)
export const selectAcceptedTeachers = (state: any) => state.teachers.filter((teacher: AdminTeacher) => !!teacher.validated)
export const selectInvitedTeachersForSchool = (schoolId: number) => (state: any) => state.teachers.filter((teacher: AdminTeacher) => !teacher.validated && teacher.schoolId === schoolId)
export const selectAcceptedTeachersForSchool = (schoolId: number) => (state: any) => state.teachers.filter((teacher: AdminTeacher) => !!teacher.validated && teacher.schoolId === schoolId)

export const TEACHER_VERIFICATION_STATUS = {
  PENDING: 'pending',
  K8_RESTRICTED: 'k8_restricted',
  KEEP_RESTRICTED: 'keep_restricted',
  APPROVED: 'approved',
  ACCOUNT_CREATED_BEFORE_NEW_PROCESS: 'account_created_before_new_process',
} as const

const initialState: AdminTeacher[] = [
  {
    fullName: null,
    firstName: null,
    lastName: null,
    emailAddress: null,
    id: null,
    schoolName: null,
    validated: null,
    validationToken: null,
    invitedDate: null,
    schoolState: null,
    schoolId: null,
    mostRecentCourseStartDate: null,
    isQASchool: null,
    teacherVerificationStatus: null,
    jobTitle: null,
    educatorType: null,
    signUpDistrict: null,
    signUpCounty: null,
    signUpState: null,
    signUpCountry: null,
    signUpSchoolName: null,
  },
]

const teachersSlice = createSlice({
  name: 'teachers',
  initialState,
  reducers: {
    loadTeachers: (state, {
      payload: teachers,
    }: PayloadAction<any[]>) => teachers.map((teacher: any) => ({
      fullName: `${teacher.firstName} ${teacher.lastName}`,
      firstName: teacher.firstName,
      lastName: teacher.lastName,
      emailAddress: teacher.email,
      id: teacher.id,
      mostRecentCourseStartDate: !!teacher.mostRecentCourseStartDate ? moment(teacher.mostRecentCourseStartDate).format('YYYY-MM-DD') : '',
      schoolName: teacher.school.name,
      schoolId: teacher.school.id,
      schoolState: teacher.school.state,
      validated: teacher.validated,
      validationToken: teacher.validationToken,
      invitedDate: moment(teacher.createdAt).format('YYYY-MM-DD'),
      isQASchool: teacher.school.isQASchool,
      teacherVerificationStatus: teacher.teacherVerificationStatus,
      jobTitle: teacher.jobTitle,
      educatorType: teacher.educatorType,
      signUpDistrict: teacher.signUpDistrict,
      signUpCounty: teacher.signUpCounty,
      signUpState: teacher.signUpState,
      signUpCountry: teacher.signUpCountry,
      signUpSchoolName: teacher.signUpSchoolName,
    })),
  },
})

export const { loadTeachers } = teachersSlice.actions

export default teachersSlice.reducer
